import { PlanSession, ValidationWarning } from '../../types/athlete';

const isHardSession = (session: PlanSession): boolean => {
  const hardCategories = ['tempo', 'threshold', 'interval', 'brick'];
  const hardZones = ['T', 'I', 'Z3', 'Z4', 'Z5', 'CSS-X'];
  
  if (hardCategories.includes(session.category)) return true;
  if (hardZones.includes(session.target_zone_type)) return true;
  return false;
};

const isLongSession = (session: PlanSession): boolean => {
  return session.is_brick || session.planned_duration_minutes >= 90 || session.has_run_walk;
};

const getPreviousDay = (day: number): number => {
  return day === 1 ? 7 : day - 1;
};

const getNextDay = (day: number): number => {
  return day === 7 ? 1 : day + 1;
};

const getLastNDays = (allSessions: PlanSession[], n: number, targetDay: number): { day: number; hasHardSession: boolean; hasLongSession: boolean }[] => {
  const days: { day: number; hasHardSession: boolean; hasLongSession: boolean }[] = [];
  
  for (let i = 0; i < n; i++) {
    const checkDay = targetDay - i;
    const actualDay = checkDay <= 0 ? 7 + checkDay : checkDay;
    const daySessions = allSessions.filter(s => s.day_of_week === actualDay);
    
    days.push({
      day: actualDay,
      hasHardSession: daySessions.some(isHardSession),
      hasLongSession: daySessions.some(isLongSession),
    });
  }
  
  return days;
};

export const validateSessionPlacement = (
  movingSession: PlanSession,
  targetDay: number,
  allSessions: PlanSession[]
): ValidationWarning[] => {
  const warnings: ValidationWarning[] = [];
  
  const sessionsOnTargetDay = allSessions.filter(s => s.day_of_week === targetDay && s.id !== movingSession.id);
  const targetDayHasLongBike = sessionsOnTargetDay.some(
    s => s.discipline === 'bike' && s.planned_duration_minutes >= 90
  );
  
  const previousDay = getPreviousDay(movingSession.day_of_week);
  const sessionsOnPreviousDay = allSessions.filter(s => s.day_of_week === previousDay);
  const previousDayHasLong = sessionsOnPreviousDay.some(isLongSession);
  
  const nextDay = getNextDay(movingSession.day_of_week);
  const sessionsOnNextDay = allSessions.filter(s => s.day_of_week === nextDay);
  const nextDayHasHard = sessionsOnNextDay.some(isHardSession);
  
  const last7Days = getLastNDays(allSessions, 7, movingSession.day_of_week);
  const consecutiveHardDays = last7Days.filter(d => d.hasHardSession).length;
  
  const weekSessions = allSessions.filter(s => s.week_number === movingSession.week_number);
  const hasRecoveryDay = weekSessions.some(s => 
    s.category === 'recovery' || s.discipline === 'strength'
  );
  
  const targetDayHasHard = sessionsOnTargetDay.some(isHardSession);
  
  if (movingSession.is_long_session && targetDayHasLongBike) {
    warnings.push({
      ruleId: 'long-run-after-long-bike',
      ruleName: 'Long Session After Long Bike',
      severity: 'error',
      message: 'Long runs should typically be on fresh legs, not after long bikes. This significantly increases injury risk.',
      suggestion: 'Move long run to a different day, or move long bike to the day before.',
    });
  }
  
  if (isHardSession(movingSession) && previousDayHasLong && !movingSession.is_brick) {
    warnings.push({
      ruleId: 'hard-after-long',
      ruleName: 'Hard After Long Session',
      severity: 'warning',
      message: 'Hard sessions after long sessions increase injury risk. Consider swapping with a recovery session.',
      suggestion: 'Move to a day after a rest day, or replace with a recovery session.',
    });
  }
  
  if (isHardSession(movingSession) && nextDayHasHard) {
    warnings.push({
      ruleId: 'consecutive-hard',
      ruleName: 'Consecutive Hard Days',
      severity: 'warning',
      message: `This creates ${consecutiveHardDays + 1} consecutive hard days. Cumulative fatigue may build up.`,
      suggestion: 'Consider adding a recovery day between hard sessions.',
    });
  }
  
  if (movingSession.category === 'endurance' && 
      movingSession.discipline === 'run' && 
      movingSession.planned_duration_minutes >= 50 &&
      targetDayHasHard &&
      !movingSession.is_brick) {
    warnings.push({
      ruleId: 'key-run-after-hard',
      ruleName: 'Key Run After Hard Session',
      severity: 'warning',
      message: 'Key/long runs should typically be on fresh legs for optimal training effect and injury prevention.',
      suggestion: 'Consider moving this run to a recovery day or after a rest day.',
    });
  }
  
  if (allSessions.length > 0 && !hasRecoveryDay && weekSessions.length > 0) {
    const recoverySessionsInWeek = weekSessions.filter(s => 
      s.category === 'recovery' || s.discipline === 'strength'
    ).length;
    
    if (recoverySessionsInWeek === 0 && movingSession.category !== 'recovery' && movingSession.discipline !== 'strength') {
      warnings.push({
        ruleId: 'no-recovery-day',
        ruleName: 'Missing Recovery Day',
        severity: 'error',
        message: 'Every week needs at least one recovery day for proper adaptation.',
        suggestion: 'Add a rest day or recovery swim/strength session.',
      });
    }
  }
  
  if (movingSession.is_brick && sessionsOnTargetDay.some(s => 
    s.discipline === 'run' && s.planned_duration_minutes >= 30 && !s.is_brick
  )) {
    warnings.push({
      ruleId: 'brick-with-run',
      ruleName: 'Brick Day With Additional Run',
      severity: 'warning',
      message: 'This day already has a brick session with a run. Adding another run may lead to overtraining.',
      suggestion: 'Consider removing the additional run or making it a recovery run.',
    });
  }
  
  return warnings;
};

export const validateWeekPlan = (
  sessions: PlanSession[]
): { valid: boolean; warnings: ValidationWarning[] } => {
  const allWarnings: ValidationWarning[] = [];
  
  const hasRecovery = sessions.some(s => 
    s.category === 'recovery' || s.discipline === 'strength'
  );
  
  if (!hasRecovery && sessions.length > 0) {
    allWarnings.push({
      ruleId: 'no-recovery-day',
      ruleName: 'Missing Recovery Day',
      severity: 'error',
      message: 'Every week needs at least one recovery day for proper adaptation.',
      suggestion: 'Add a rest day or recovery swim/strength session.',
    });
  }
  
  const longBikeDays: number[] = [];
  const longRunDays: number[] = [];
  
  sessions.forEach(s => {
    if (s.discipline === 'bike' && s.planned_duration_minutes >= 90) {
      longBikeDays.push(s.day_of_week);
    }
    if (s.is_long_session && s.discipline === 'run') {
      longRunDays.push(s.day_of_week);
    }
  });
  
  longRunDays.forEach(runDay => {
    if (longBikeDays.includes(runDay)) {
      allWarnings.push({
        ruleId: 'long-run-after-long-bike',
        ruleName: 'Long Run Same Day As Long Bike',
        severity: 'error',
        message: 'Long run on same day as long bike significantly increases injury risk.',
        suggestion: 'Move long run to a different day.',
      });
    }
  });
  
  const hardDays: number[] = sessions.filter(s => isHardSession(s)).map(s => s.day_of_week);
  let consecutiveHard = 0;
  for (let d = 1; d <= 7; d++) {
    if (hardDays.includes(d)) {
      consecutiveHard++;
      if (consecutiveHard >= 3) {
        allWarnings.push({
          ruleId: 'consecutive-hard-days',
          ruleName: 'Multiple Consecutive Hard Days',
          severity: 'warning',
          message: `${consecutiveHard} consecutive hard days detected. Consider adding recovery.`,
          suggestion: 'Spread out hard sessions with recovery days.',
        });
        break;
      }
    } else {
      consecutiveHard = 0;
    }
  }
  
  return {
    valid: !allWarnings.some(w => w.severity === 'error'),
    warnings: allWarnings,
  };
};

export const getSuggestedAlternativeDays = (
  session: PlanSession,
  allSessions: PlanSession[]
): number[] => {
  const suggestions: number[] = [];
  const conflictingDays = new Set<number>();
  
  allSessions.forEach(s => {
    if (isHardSession(session) && isLongSession(s)) {
      conflictingDays.add(getPreviousDay(s.day_of_week));
      conflictingDays.add(s.day_of_week);
    }
    if (isLongSession(session) && s.discipline === 'bike' && s.planned_duration_minutes >= 90) {
      conflictingDays.add(s.day_of_week);
    }
    if (session.is_long_session && s.is_long_session) {
      conflictingDays.add(s.day_of_week);
    }
  });
  
  for (let day = 1; day <= 7; day++) {
    if (!conflictingDays.has(day)) {
      suggestions.push(day);
    }
  }
  
  return suggestions.length > 0 ? suggestions : [1, 2, 3, 4, 5, 6, 7];
};
